import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { CheckCircle, XCircle, Loader2, Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const verifyEmailTexts = {
    de: {
        verifyingMessage: 'Deine E-Mail-Adresse wird verifiziert...',
        missingToken: 'Verifizierungstoken fehlt.',
        successMessage: 'E-Mail erfolgreich verifiziert.',
        failedMessage: 'Verifizierung fehlgeschlagen. Der Link ist möglicherweise ungültig oder abgelaufen.',
        verifyingTitle: 'E-Mail wird verifiziert',
        successTitle: 'E-Mail verifiziert',
        failedTitle: 'Verifizierung fehlgeschlagen',
        continueToLogin: 'Weiter zum Login',
        createNewAccount: 'Neues Konto erstellen',
        backToLogin: 'Zurück zum Login',
        poweredBy: 'Powered by VMS Recording',
    },

    en: {
        verifyingMessage: 'Verifying your email address...',
        missingToken: 'Verification token is missing.',
        successMessage: 'Email verified successfully.',
        failedMessage: 'Verification failed. The link may be invalid or expired.',
        verifyingTitle: 'Verifying email',
        successTitle: 'Email verified',
        failedTitle: 'Verification failed',
        continueToLogin: 'Continue to login',
        createNewAccount: 'Create new account',
        backToLogin: 'Back to login',
        poweredBy: 'Powered by VMS Recording',
    },

    no: {
        verifyingMessage: 'Bekrefter e-postadressen din...',
        missingToken: 'Verifiseringstoken mangler.',
        successMessage: 'E-postadressen er bekreftet.',
        failedMessage: 'Verifisering mislyktes. Lenken kan være ugyldig eller utløpt.',
        verifyingTitle: 'Bekrefter e-post',
        successTitle: 'E-post bekreftet',
        failedTitle: 'Verifisering mislyktes',
        continueToLogin: 'Fortsett til innlogging',
        createNewAccount: 'Opprett ny konto',
        backToLogin: 'Tilbake til innlogging',
        poweredBy: 'Powered by VMS Recording',
    },

    ko: {
        verifyingMessage: '이메일 주소를 확인하는 중...',
        missingToken: '인증 토큰이 없습니다.',
        successMessage: '이메일이 성공적으로 인증되었습니다.',
        failedMessage: '인증에 실패했습니다. 링크가 잘못되었거나 만료되었을 수 있습니다.',
        verifyingTitle: '이메일 인증 중',
        successTitle: '이메일 인증 완료',
        failedTitle: '인증 실패',
        continueToLogin: '로그인으로 계속',
        createNewAccount: '새 계정 만들기',
        backToLogin: '로그인으로 돌아가기',
        poweredBy: 'Powered by VMS Recording',
    },

    fi: {
        verifyingMessage: 'Vahvistetaan sähköpostiosoitettasi...',
        missingToken: 'Vahvistustunnus puuttuu.',
        successMessage: 'Sähköposti vahvistettu onnistuneesti.',
        failedMessage: 'Vahvistus epäonnistui. Linkki voi olla virheellinen tai vanhentunut.',
        verifyingTitle: 'Vahvistetaan sähköpostia',
        successTitle: 'Sähköposti vahvistettu',
        failedTitle: 'Vahvistus epäonnistui',
        continueToLogin: 'Jatka kirjautumiseen',
        createNewAccount: 'Luo uusi tili',
        backToLogin: 'Takaisin kirjautumiseen',
        poweredBy: 'Powered by VMS Recording',
    },
};

const VerifyEmail = () => {
    const [searchParams] = useSearchParams();
    const hasVerifiedRef = useRef(false);
    const { language } = useLanguage();

    const text = verifyEmailTexts[language] || verifyEmailTexts.en;

    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState(text.verifyingMessage);

    useEffect(() => {
        setMessage(text.verifyingMessage);
    }, [text.verifyingMessage]);

    useEffect(() => {
        const verifyEmail = async () => {
            if (hasVerifiedRef.current) return;
            hasVerifiedRef.current = true;

            const token = searchParams.get('token');

            if (!token) {
                setStatus('error');
                setMessage(text.missingToken);
                return;
            }

            try {
                const response = await axios.get(`${API}/auth/verify-email`, {
                    params: { token },
                    withCredentials: true,
                });

                setStatus('success');
                setMessage(response.data?.message || text.successMessage);
            } catch (error) {
                const backendMessage =
                    error.response?.data?.detail ||
                    error.response?.data?.message ||
                    text.failedMessage;

                setStatus('error');
                setMessage(backendMessage);
            }
        };

        verifyEmail();
    }, [searchParams, text]);

    const isSuccess = status === 'success';
    const isLoading = status === 'loading';
    const isError = status === 'error';

    return (
        <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
            <div className="mb-10 flex items-center gap-4">
                <div className="text-cyan-400">
                    <Radio size={48} strokeWidth={2.5} />
                </div>

                <h1 className="text-4xl md:text-5xl font-black tracking-tight">
                    WORLDofRADIO
                </h1>
            </div>

            <div className="w-full max-w-2xl rounded-[2rem] border border-white/10 bg-black/70 shadow-2xl px-8 py-10 md:px-12 md:py-14 text-center">
                <div className="flex justify-center mb-8">
                    {isLoading && (
                        <div className="w-24 h-24 rounded-full border border-cyan-400/30 bg-cyan-400/10 flex items-center justify-center">
                            <Loader2 className="text-cyan-400 animate-spin" size={48} />
                        </div>
                    )}

                    {isSuccess && (
                        <div className="w-24 h-24 rounded-full border border-cyan-400/40 bg-cyan-400/10 flex items-center justify-center">
                            <CheckCircle className="text-cyan-400" size={52} />
                        </div>
                    )}

                    {isError && (
                        <div className="w-24 h-24 rounded-full border border-red-400/40 bg-red-500/10 flex items-center justify-center">
                            <XCircle className="text-red-400" size={52} />
                        </div>
                    )}
                </div>

                <h2 className="text-3xl md:text-4xl font-black mb-4">
                    {isLoading && text.verifyingTitle}
                    {isSuccess && text.successTitle}
                    {isError && text.failedTitle}
                </h2>

                <p className="text-gray-400 text-lg leading-relaxed mb-10">
                    {message}
                </p>

                {isSuccess ? (
                    <Link
                        to="/login"
                        className="inline-flex items-center justify-center rounded-2xl bg-cyan-400 hover:bg-cyan-300 text-black font-bold px-8 py-4 transition-colors"
                    >
                        {text.continueToLogin}
                    </Link>
                ) : (
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/register"
                            className="inline-flex items-center justify-center rounded-2xl bg-cyan-400 hover:bg-cyan-300 text-black font-bold px-8 py-4 transition-colors"
                        >
                            {text.createNewAccount}
                        </Link>

                        <Link
                            to="/login"
                            className="inline-flex items-center justify-center rounded-2xl border border-white/15 hover:border-cyan-400/60 text-white font-bold px-8 py-4 transition-colors"
                        >
                            {text.backToLogin}
                        </Link>
                    </div>
                )}
            </div>

            <div className="mt-10 text-sm text-gray-500">
                {text.poweredBy}
            </div>
        </div>
    );
};

export default VerifyEmail;